
import React, { useEffect, useState, useRef } from 'react';
import { Card, Button } from '../components/UI';
import { apiRequestFull, apiRequest } from '../utils/api';
import { API_BASE_URL } from '../config';
import { Save, RefreshCw, Database, X, Flag, Timer, Zap, Search, FileSpreadsheet, Download, Activity, TrendingUp, Layers, Upload, AlertOctagon, Shield, Users, FileJson, AlertTriangle, Trash2, ChevronDown, Calendar, ArrowDown } from 'lucide-react';
import { ResultRow, Team } from '../types';
import { useLeague } from '../components/LeagueContext';
import { PageHero } from '../components/PageHero';

interface EventOption {
    id: string;
    trackLocation: string;
    trackName: string;
    isSprint: boolean;
    date: string;
    status: string;
    roundNumber: number;
}

interface DriverPoolItem {
    user_id: string;
    username: string;
    eaId?: string;
    teamId?: string;
    teamName: string;
    teamColor: string;
}

interface TelemetryStats {
    laps: number[];
    stints: { tyre: string; laps: number }[];
    penalties: number;
    warnings: number;
    best_sectors?: { s1: number; s2: number; s3: number };
    theoretical_best?: number;
    consistency?: number;
    race_pace?: number;
    lap_history?: any[];
}

interface ExtendedResultRow extends ResultRow {
    time_diff?: string;
    quali_time?: string;
    sprint_time?: string;
    quali_status?: string;
    sprint_status?: string;
    telemetry_data?: TelemetryStats | null;
    clean_race_bonus?: boolean; // NEW: +1 Point
}

// Helper: Levenshtein Distance for Fuzzy Matching
const getLevenshteinDistance = (a: string, b: string): number => {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    const matrix = Array.from({ length: b.length + 1 }, () => [0]);
    for (let i = 0; i <= b.length; i++) matrix[i][0] = i;
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
            const cost = a[j - 1] === b[i - 1] ? 0 : 1;
            matrix[i][j] = Math.min(
                matrix[i - 1][j] + 1,
                matrix[i][j - 1] + 1,
                matrix[i - 1][j - 1] + cost
            );
        }
    }
    return matrix[b.length][a.length];
};

const formatTime = (seconds: number) => {
    if (!seconds) return '-';
    const m = Math.floor(seconds / 60);
    const s = (seconds % 60).toFixed(3);
    return `${m}:${s.padStart(6, '0')}`;
};

const calculateStdDev = (array: number[]) => {
    if (array.length < 2) return 0;
    const n = array.length;
    const mean = array.reduce((a, b) => a + b) / n;
    return Math.sqrt(array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n);
};

const DriverSelect: React.FC<{
    value: string;
    pool: DriverPoolItem[];
    onSelect: (userId: string) => void;
    placeholder?: string;
    error?: boolean;
}> = ({ value, pool, onSelect, placeholder, error }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const selectedDriver = pool.find(d => d.user_id === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = pool.filter(d => {
        const s = search.toLowerCase();
        return (d.username || '').toLowerCase().includes(s) || (d.teamName || '').toLowerCase().includes(s);
    });

    const handleSelect = (id: string) => { onSelect(id); setIsOpen(false); setSearch(''); };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            {selectedDriver ? (
                <div className="flex items-center justify-between bg-gray-800 border border-gray-600 rounded p-2 cursor-pointer hover:border-gray-500" onClick={() => { setIsOpen(!isOpen); setSearch(''); }}>
                    <div className="flex items-center gap-3">
                        <div className="w-1 h-6 rounded-full" style={{backgroundColor: selectedDriver.teamColor}}></div>
                        <div>
                            <div className="font-bold text-white leading-tight">{selectedDriver.username}</div>
                            <div className="text-[10px] text-gray-400 uppercase">{selectedDriver.teamName}</div>
                        </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); onSelect(''); }} className="p-1 text-gray-500 hover:text-white"><X size={14}/></button>
                </div>
            ) : (
                <div className="relative">
                    <input 
                        type="text"
                        className={`w-full bg-gray-900 border rounded p-2 pl-9 text-white outline-none placeholder-gray-500 transition-colors ${error ? 'border-red-500 focus:border-red-500 bg-red-900/10' : 'border-gray-700 focus:border-f1-red'}`}
                        placeholder={placeholder || "Fahrer suchen..."}
                        value={search}
                        onChange={e => { setSearch(e.target.value); setIsOpen(true); }}
                        onFocus={() => setIsOpen(true)}
                    />
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${error ? 'text-red-500' : 'text-gray-600'}`}/>
                    {error && <AlertTriangle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500 pointer-events-none" />}
                </div>
            )}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded z-50 max-h-60 overflow-y-auto shadow-xl">
                    {filtered.length > 0 ? filtered.map(d => (
                        <div key={d.user_id} onClick={() => handleSelect(d.user_id)} className="p-2 border-b border-gray-700 hover:bg-gray-700 cursor-pointer flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{backgroundColor: d.teamColor}}></div>
                            <div>
                                <div className="font-bold text-sm text-gray-200">{d.username}</div>
                                <div className="text-xs text-gray-500">{d.teamName}</div>
                            </div>
                        </div>
                    )) : <div className="p-3 text-xs text-gray-500 italic text-center">Keine Fahrer gefunden</div>}
                </div>
            )}
        </div>
    );
};

// Result Row with explicit team selector
const ResultRowSlot: React.FC<{
    position: number;
    data: ExtendedResultRow;
    pool: DriverPoolItem[]; 
    teams: Team[];
    activeTab: 'QUALI' | 'SPRINT' | 'RACE';
    onChange: (field: string, value: any) => void;
    onMove: (targetPos: number) => void;
    totalFilled: number;
}> = ({ position, data, pool, teams, activeTab, onChange, onMove, totalFilled }) => {
    const [showMoveInput, setShowMoveInput] = useState(false);
    const [moveTarget, setMoveTarget] = useState(position.toString());
    
    const { user_id, status, fastest_lap, clean_race_bonus, time_diff, quali_time, sprint_time, team_id, telemetry_data } = data;

    // FIX: Determine which time to display based on active tab
    let displayTime = time_diff;
    if (activeTab === 'QUALI') displayTime = quali_time;
    if (activeTab === 'SPRINT') displayTime = sprint_time;

    // Validation: Has time/data but no driver?
    const isMissingDriver = !user_id && (!!displayTime || (telemetry_data && telemetry_data.laps.length > 0));

    const statusField = activeTab === 'RACE' ? 'status' : activeTab === 'QUALI' ? 'quali_status' : 'sprint_status';
    const currentStatus = data[statusField as keyof ExtendedResultRow] as string;
    const isFinished = currentStatus === 'FINISHED';

    const getStatusColor = () => {
        if (currentStatus === 'FINISHED') return 'text-green-500';
        if (currentStatus === 'DNF') return 'text-red-500';
        if (currentStatus === 'DSQ') return 'text-gray-500 line-through';
        return 'text-yellow-500';
    };

    const timeField = activeTab === 'RACE' ? 'time_diff' : activeTab === 'QUALI' ? 'quali_time' : 'sprint_time';

    const getTyreColor = (t: string) => {
        const c = t.toUpperCase();
        if(c.includes('SOFT') || c==='S') return 'text-red-500 border-red-500';
        if(c.includes('MEDIUM') || c==='M') return 'text-yellow-500 border-yellow-500';
        if(c.includes('HARD') || c==='H') return 'text-white border-gray-300';
        if(c.includes('INTER') || c==='I') return 'text-green-500 border-green-500';
        if(c.includes('WET') || c==='W') return 'text-blue-500 border-blue-500';
        return 'text-gray-500 border-gray-500';
    };

    // Position Styling
    let posBg = "bg-gray-800 text-gray-400";
    if (position === 1) posBg = "bg-yellow-500 text-black shadow-lg shadow-yellow-500/20";
    else if (position === 2) posBg = "bg-gray-300 text-black";
    else if (position === 3) posBg = "bg-orange-700 text-white";

    return (
        <div className="mb-2">
            <div className={`flex items-center gap-2 p-2 bg-gray-900/50 rounded-t border transition-colors ${isMissingDriver ? 'border-red-500 bg-red-900/5' : 'border-gray-800/50 hover:border-gray-700'}`}>
                <div className="w-10 flex-shrink-0 flex flex-col items-center gap-0.5">
                    <div className={`w-8 h-8 flex items-center justify-center rounded font-black font-mono text-sm ${posBg} cursor-pointer`} onClick={() => { if(user_id) { setShowMoveInput(!showMoveInput); setMoveTarget(position.toString()); } }} title={user_id ? "Klicken zum Verschieben" : ""}>
                        P{position}
                    </div>
                    {showMoveInput && user_id && (
                        <div className="flex items-center gap-0.5 mt-0.5">
                            <input 
                                type="number" min={1} max={20}
                                className="w-8 h-5 bg-gray-800 border border-blue-500/50 rounded text-[10px] text-center text-white outline-none"
                                value={moveTarget}
                                onChange={e => setMoveTarget(e.target.value)}
                                onKeyDown={e => { if(e.key === 'Enter') { const t = parseInt(moveTarget); if(t >= 1 && t <= 20 && t !== position) { onMove(t); setShowMoveInput(false); } } }}
                                autoFocus
                            />
                            <button onClick={() => { const t = parseInt(moveTarget); if(t >= 1 && t <= 20 && t !== position) { onMove(t); setShowMoveInput(false); } }} className="w-5 h-5 bg-blue-600 hover:bg-blue-500 rounded flex items-center justify-center"><ArrowDown size={10} className="text-white"/></button>
                        </div>
                    )}
                </div>
                
                <div className="flex-1 min-w-[200px] flex flex-col gap-1">
                    <DriverSelect 
                        value={user_id} 
                        pool={pool} 
                        onSelect={(id) => onChange('user_id', id)} 
                        placeholder={isMissingDriver ? "Fahrer zuweisen!" : `Fahrer P${position}...`}
                        error={isMissingDriver}
                    />
                    {/* Manual Team Selector if no user or just to override */}
                    <div className="flex items-center gap-1">
                        <select 
                            className="bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-[10px] text-gray-400 w-full"
                            value={team_id || ''}
                            onChange={(e) => onChange('team_id', e.target.value)}
                        >
                            <option value="">{user_id ? 'Auto-Detect Team' : 'Select Team (Required)'}</option>
                            {teams.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="w-24">
                    <input 
                        type="text" 
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-white focus:border-f1-red outline-none" 
                        placeholder="Zeit/Gap" 
                        value={displayTime || ''} 
                        onChange={e => onChange(timeField, e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <select 
                        className={`bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs font-bold outline-none ${getStatusColor()}`} 
                        value={currentStatus} 
                        onChange={e => onChange(statusField, e.target.value)}
                    >
                        <option value="FINISHED">FIN</option>
                        <option value="DNF">DNF</option>
                        <option value="DNS">DNS</option>
                        <option value="DSQ">DSQ</option>
                    </select>
                    {activeTab === 'RACE' && (
                        <>
                            <button 
                                onClick={() => isFinished && onChange('fastest_lap', !fastest_lap)} 
                                disabled={!isFinished}
                                className={`w-8 h-8 rounded border flex items-center justify-center transition-colors ${!isFinished ? 'opacity-30 cursor-not-allowed border-gray-800' : fastest_lap ? 'bg-purple-900/80 border-purple-500 text-purple-400 cursor-pointer' : 'bg-gray-800 border-gray-700 text-gray-700 hover:text-gray-500 cursor-pointer'}`} 
                                title={!isFinished ? "Nur bei Zielankunft möglich" : "Fastest Lap"}
                            >
                                <Timer size={16}/>
                            </button>
                            <button 
                                onClick={() => isFinished && onChange('clean_race_bonus', !clean_race_bonus)} 
                                disabled={!isFinished}
                                className={`w-8 h-8 rounded border flex items-center justify-center transition-colors ${!isFinished ? 'opacity-30 cursor-not-allowed border-gray-800' : clean_race_bonus ? 'bg-blue-900/80 border-blue-500 text-blue-400 cursor-pointer' : 'bg-gray-800 border-gray-700 text-gray-700 hover:text-gray-500 cursor-pointer'}`} 
                                title={!isFinished ? "Nur bei Zielankunft möglich" : "Clean Race (+1 Pt)"}
                            >
                                <Shield size={16}/>
                            </button>
                        </>
                    )}
                </div>
            </div>
            {telemetry_data && (
                <div className="px-3 py-1.5 bg-gray-900 border-x border-b border-gray-800 rounded-b flex flex-wrap gap-4 items-center text-[10px] text-gray-400 mb-1 ml-12">
                    {telemetry_data.penalties > 0 && (
                        <div className="flex items-center gap-1 text-red-400 font-bold bg-red-900/20 px-1.5 py-0.5 rounded border border-red-900/50" title="Zeitstrafen">
                            <AlertOctagon size={10}/>
                            <span>+{telemetry_data.penalties}s Pen</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1" title="Konstanz">
                        <Activity size={10} className="text-blue-400"/><span className="font-mono text-gray-300">{telemetry_data.consistency ? telemetry_data.consistency.toFixed(3) : '-'}s</span>
                    </div>
                    <div className="flex items-center gap-1" title="Theoretische Bestzeit">
                        <Zap size={10} className="text-yellow-500"/><span className="font-mono text-gray-300">{telemetry_data.theoretical_best ? formatTime(telemetry_data.theoretical_best) : '-'}</span>
                    </div>
                    <div className="flex items-center gap-1" title="Reifenstrategie">
                        <Layers size={10} className="text-gray-500"/>
                        <div className="flex gap-1">
                            {Array.isArray(telemetry_data.stints) && telemetry_data.stints.map((s: any, idx: number) => (
                                <span key={idx} className={`px-1 rounded-sm border ${getTyreColor(s.tyre || s.visual_name || s.compound_name || '?')}`}>{(s.tyre || s.visual_name || s.compound_name || '?').charAt(0)}<span className="opacity-60 ml-0.5">{s.laps || s.end_lap || ''}</span></span>
                            ))}
                        </div>
                    </div>
                    {telemetry_data.race_pace && (
                        <div className="flex items-center gap-1 ml-auto" title="Race Pace"><TrendingUp size={10} className="text-green-500"/><span className="font-mono font-bold text-white">{formatTime(telemetry_data.race_pace)}</span></div>
                    )}
                </div>
            )}
        </div>
    );
};

export const ResultsAdmin: React.FC = () => {
    const { seasons } = useLeague();
    const [events, setEvents] = useState<EventOption[]>([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState<string>('');
    const [selectedEventId, setSelectedEventId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const csvInputRef = useRef<HTMLInputElement>(null);
    const jsonInputRef = useRef<HTMLInputElement>(null); 
    
    const [allDriversPool, setAllDriversPool] = useState<DriverPoolItem[]>([]);
    const [masterDriverList, setMasterDriverList] = useState<DriverPoolItem[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [slots, setSlots] = useState<ExtendedResultRow[]>([]);
    const [activeTab, setActiveTab] = useState<'QUALI' | 'SPRINT' | 'RACE'>('RACE');
    const [showTelemetryModal, setShowTelemetryModal] = useState(false);
    const [telemetrySessions, setTelemetrySessions] = useState<any[]>([]);
    const [selectedArchiveId, setSelectedArchiveId] = useState<number | null>(null);

    useEffect(() => {
        const url = selectedSeasonId ? `events.php?seasonId=${selectedSeasonId}` : 'events.php';
        apiRequestFull<any[]>(url).then(res => { if(res.data) setEvents(res.data); });
    }, [selectedSeasonId]);

    useEffect(() => {
        apiRequest<any>('standings.php').then(res => {
            if(res && res.drivers) {
                const pool: DriverPoolItem[] = res.drivers.map((d: any) => ({
                    user_id: d.id, username: d.username, eaId: d.ea_id, teamId: d.teamId, teamName: d.teamName || 'Unknown', teamColor: d.teamColor || '#666'
                }));
                setMasterDriverList(pool);
            }
        });
        apiRequest<Team[]>('teams.php').then(data => setTeams(data || []));
    }, []);

    const initSlots = (count: number): ExtendedResultRow[] => {
        return Array.from({ length: count }).map((_, i) => ({
            user_id: '', username: '', team_id: '', teamName: '', position: i + 1, status: 'FINISHED' as const, quali_status: 'FINISHED', sprint_status: 'FINISHED', fastest_lap: false, clean_race_bonus: false, time_diff: '', quali_time: '', sprint_time: '', telemetry_data: null
        }));
    };

    const loadResults = async (eventId: string) => {
        if (!eventId) { setSlots([]); return; }
        setLoading(true);
        setSelectedEventId(eventId);
        setSelectedArchiveId(null);
        try {
            const res = await apiRequestFull<ExtendedResultRow[]>(`results.php?eventId=${eventId}`);
            const poolMap = new Map<string, DriverPoolItem>();
            masterDriverList.forEach(d => poolMap.set(d.user_id, d));
            if (res.data) {
                res.data.forEach((r: any) => {
                    poolMap.set(r.user_id, {
                        user_id: r.user_id, username: r.username, eaId: r.ea_id, teamId: r.team_id, teamName: r.teamName || 'Unknown', teamColor: r.teamColor || '#666'
                    });
                });
            }
            setAllDriversPool(Array.from(poolMap.values()));
            let newSlots = initSlots(20);
            if (res.data && res.data.length > 0) {
                res.data.forEach(row => {
                    let pos = 999;
                    if (activeTab === 'RACE') pos = Number(row.position);
                    else if (activeTab === 'QUALI') pos = Number(row.quali_position);
                    else if (activeTab === 'SPRINT') pos = Number(row.sprint_position);
                    if (pos > 0 && pos <= 20) {
                        newSlots[pos - 1] = { 
                            ...newSlots[pos - 1], 
                            user_id: row.user_id,
                            team_id: row.team_id,
                            status: row.status, 
                            quali_status: row.quali_status || 'FINISHED', 
                            sprint_status: row.sprint_status || 'FINISHED', 
                            fastest_lap: !!row.fastest_lap, 
                            clean_race_bonus: (row.clean_race_bonus as any) == 1 || (row.clean_race_bonus as any) === true, 
                            time_diff: row.time_diff || '', 
                            quali_time: row.quali_time || '', 
                            sprint_time: row.sprint_time || '', 
                            telemetry_data: row.telemetry_data 
                        };
                    }
                });
            } 
            setSlots(newSlots);
        } catch (e: any) { alert("Fehler: " + e.message); } finally { setLoading(false); }
    };

    useEffect(() => { if(selectedEventId) loadResults(selectedEventId); }, [activeTab]);

    const handleSlotChange = (index: number, field: string, value: any) => {
        let newSlots = [...slots];
        
        // 1. Update the value
        newSlots[index] = { ...newSlots[index], [field]: value };

        // --- FIX: ENFORCE MUTUAL EXCLUSIVITY FOR FASTEST LAP ---
        if (field === 'fastest_lap' && value === true) {
             // Reset all others to false
             newSlots = newSlots.map((s, i) => 
                 i === index ? s : { ...s, fastest_lap: false }
             );
        }
        
        // 2. Handle Side Effects (Status Change)
        const isStatusField = ['status', 'quali_status', 'sprint_status'].includes(field);
        
        if (isStatusField && value !== 'FINISHED') {
             // If status becomes DNF/DSQ/DNS:
             // - Reset Bonuses & Times for this row
             const row = newSlots[index];
             row.fastest_lap = false;
             row.clean_race_bonus = false;
             
             if (activeTab === 'RACE') row.time_diff = '';
             else if (activeTab === 'QUALI') row.quali_time = '';
             else if (activeTab === 'SPRINT') row.sprint_time = '';
             
             // - Reorder Logic: Move this row to the bottom of the "filled" drivers list
             newSlots.splice(index, 1); // Remove from current pos
             
             // Separate filled slots (with user_id) from empty slots
             const filled = newSlots.filter(s => s.user_id);
             const empty = newSlots.filter(s => !s.user_id);
             
             // Place the moved row at the end of the filled list
             newSlots = [...filled, row, ...empty];
             
             // Ensure we maintain array length (fill with empty if needed, though split/join preserves count)
             while(newSlots.length < 20) {
                 newSlots.push({
                     user_id: '', username: '', team_id: '', teamName: '', position: newSlots.length + 1, status: 'FINISHED', quali_status: 'FINISHED', sprint_status: 'FINISHED', fastest_lap: false, clean_race_bonus: false, time_diff: '', quali_time: '', sprint_time: '', telemetry_data: null
                 });
             }
             
             // Re-assign positions (visual index + 1)
             newSlots = newSlots.map((s, i) => ({...s, position: i + 1}));
        }

        setSlots(newSlots);
    };

    const moveSlotToPosition = (fromIndex: number, targetPosition: number) => {
        const targetIndex = targetPosition - 1;
        if (targetIndex < 0 || targetIndex >= slots.length || fromIndex === targetIndex) return;
        
        const newSlots = [...slots];
        const movedItem = newSlots[fromIndex];
        
        // Remove from current position
        newSlots.splice(fromIndex, 1);
        // Insert at target position
        newSlots.splice(targetIndex, 0, movedItem);
        // Re-assign positions
        setSlots(newSlots.map((s, i) => ({ ...s, position: i + 1 })));
    };

    const handleReset = async () => {
        if (!selectedEventId) return;
        if (!confirm("WARNUNG: Dies löscht ALLE Ergebnisse (Quali, Sprint, Rennen) für dieses Event unwiderruflich.\n\nDas Event wird wieder auf 'Offen' gesetzt. Fortfahren?")) return;

        setLoading(true);
        try {
            await apiRequest(`results.php?eventId=${selectedEventId}`, 'DELETE');
            alert("Ergebnisse zurückgesetzt.");
            setSlots(initSlots(20)); // Reset UI
            loadResults(selectedEventId); // Optional refetch to update internal state/pool
        } catch (e: any) {
            alert("Fehler: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        setLoading(true);
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('csv_file', file);
        
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const res = await fetch(`${baseUrl}/php/import_csv.php`, {
                method: 'POST',
                body: formData
            });
            const json = await res.json();
            
            if (json.status === 'success' && json.data) {
                const imported = json.data;
                const newSlots = [...slots];

                imported.forEach((row: any) => {
                    const pos = Number(row.position);
                    if (pos > 0 && pos <= 20) {
                        const idx = pos - 1;
                        if (row.user_id) newSlots[idx].user_id = row.user_id;
                        
                        if(activeTab === 'RACE') {
                            newSlots[idx].status = row.status;
                            newSlots[idx].time_diff = row.time_diff;
                            newSlots[idx].fastest_lap = row.fastest_lap;
                        } else if(activeTab === 'QUALI') {
                             newSlots[idx].quali_status = row.status;
                             newSlots[idx].quali_time = row.time_diff;
                        } else {
                             newSlots[idx].sprint_status = row.status;
                             newSlots[idx].sprint_time = row.time_diff;
                        }

                        if(row.telemetry_data) {
                            newSlots[idx].telemetry_data = row.telemetry_data;
                            // Strict Logic: Only finished = Clean Race
                            if (row.telemetry_data.penalties === 0 && row.status === 'FINISHED' && activeTab === 'RACE') {
                                newSlots[idx].clean_race_bonus = true;
                            } else {
                                newSlots[idx].clean_race_bonus = false;
                            }
                        }
                    }
                });
                setSlots(newSlots);
                alert("CSV Daten geladen! Bitte Zuweisungen prüfen.");
            } else {
                alert("Import Fehler: " + json.message);
            }
        } catch(err: any) {
            alert("Upload Error: " + err.message);
        } finally {
            setLoading(false);
            if(csvInputRef.current) csvInputRef.current.value = '';
        }
    };
    
    const handleJsonUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        setLoading(true);
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('json_file', file);
        
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const res = await fetch(`${baseUrl}/php/telemetry.php`, {
                method: 'POST',
                body: formData
            });
            const json = await res.json();
            
            if (json.status === 'success') {
                alert("Datei erfolgreich in Datenbank importiert. Wähle sie nun aus der Liste aus.");
                loadTelemetrySessions(); // Reload list
            } else {
                alert("Upload Fehler: " + json.message);
            }
        } catch(err: any) {
            alert("Network Error: " + err.message);
        } finally {
            setLoading(false);
            if(jsonInputRef.current) jsonInputRef.current.value = '';
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const currentDbData = await apiRequestFull<ExtendedResultRow[]>(`results.php?eventId=${selectedEventId}`);
            const mergedMap = new Map<string, any>();
            (currentDbData.data || []).forEach(r => mergedMap.set(r.user_id, { ...r }));
            slots.forEach((slot, index) => {
                if (!slot.user_id && !slot.team_id) return;
                
                const poolDriver = allDriversPool.find(d => d.user_id === slot.user_id);
                const key = slot.user_id || `manual_${index}`;
                const existing = mergedMap.get(key) || { user_id: slot.user_id };
                
                if (slot.team_id) existing.team_id = slot.team_id;
                else if (poolDriver && poolDriver.teamId) existing.team_id = poolDriver.teamId;
                
                if (activeTab === 'RACE') { 
                    existing.position = index + 1; 
                    existing.status = slot.status; 
                    existing.fastest_lap = slot.fastest_lap; 
                    existing.clean_race_bonus = slot.clean_race_bonus; 
                    existing.time_diff = slot.time_diff; 
                    if (slot.telemetry_data) existing.telemetry_data = slot.telemetry_data; 
                }
                else if (activeTab === 'QUALI') { existing.quali_position = index + 1; existing.quali_time = slot.quali_time; existing.quali_status = slot.quali_status; }
                else if (activeTab === 'SPRINT') { existing.sprint_position = index + 1; existing.sprint_time = slot.sprint_time; existing.sprint_status = slot.sprint_status; }
                mergedMap.set(key, existing);
            });

            // FILTER PAYLOAD: Only send entries that have a User ID (Database Constraint)
            const payload = Array.from(mergedMap.values()).filter(r => 
                r.user_id && // MUST HAVE USER ID
                ((r.position && r.position < 900) || (r.quali_position && r.quali_position < 900) || (r.sprint_position && r.sprint_position < 900))
            );
            
            if (payload.length === 0) {
                 alert("Keine gültigen Ergebnisse zum Speichern (Fahrer müssen zugewiesen sein).");
                 setSaving(false);
                 return;
            }

            await apiRequest('results.php', 'POST', { 
                eventId: selectedEventId, 
                results: payload,
                archiveId: selectedArchiveId 
            });
            
            alert("Gespeichert!");
            loadResults(selectedEventId);
        } catch (e: any) { alert("Fehler: " + e.message); } finally { setSaving(false); }
    };

    const loadTelemetrySessions = async () => {
        try {
            const res = await apiRequest<any[]>('telemetry.php');
            setTelemetrySessions(res || []);
            setShowTelemetryModal(true);
        } catch(e: any) { alert("Fehler: " + e.message); }
    };

    const importTelemetrySession = async (sessionId: number) => {
        setLoading(true);
        setSelectedArchiveId(sessionId);
        
        try {
            // UPDATED: Using raw fetch mode to handle large JSON responses safely
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const url = `${baseUrl}/telemetry.php?id=${sessionId}&mode=raw`;
            
            const rawRes = await fetch(url);
            if (!rawRes.ok) throw new Error(`Server Fehler: ${rawRes.status}`);
            
            const text = await rawRes.text();
            let root;
            try {
                root = JSON.parse(text);
            } catch (e) {
                throw new Error("Fehler beim Parsen der Telemetrie-Datei. JSON ist ungültig.");
            }

            // Adjust root to point to the actual data object if it's wrapped
            if (root.data && typeof root.data === 'object') root = root.data; 

            const newSlots = initSlots(20);
            
            // --- ENHANCED DRIVER MATCHING ---
            const findDriverId = (rawName: string) => {
                if (!rawName) return '';
                const name = rawName.toLowerCase().trim();
                
                // 1. Exact Match (Priority)
                let match = masterDriverList.find(d => 
                    d.username.toLowerCase() === name || 
                    (d.eaId && d.eaId.toLowerCase() === name)
                );
                if (match) return match.user_id;

                // 2. Normalized Match (remove special chars/spaces)
                const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
                const normName = normalize(name);
                
                if (normName.length > 2) {
                    match = masterDriverList.find(d => {
                        const normUser = normalize(d.username);
                        const normEa = d.eaId ? normalize(d.eaId) : '';
                        return normUser === normName || (normEa && normEa === normName);
                    });
                    if (match) return match.user_id;
                }

                // 3. Substring Match
                match = masterDriverList.find(d => {
                    const u = d.username.toLowerCase();
                    return u.includes(name) || name.includes(u);
                });
                if (match) return match.user_id;

                // 4. Fuzzy Match (Levenshtein Distance)
                let bestDist = 999;
                let bestCand = null;

                for (const d of masterDriverList) {
                    const distUser = getLevenshteinDistance(name, d.username.toLowerCase());
                    const distEa = d.eaId ? getLevenshteinDistance(name, d.eaId.toLowerCase()) : 999;
                    
                    const localBest = Math.min(distUser, distEa);
                    const threshold = Math.max(3, Math.floor(name.length * 0.4)); // 40% tolerance

                    if (localBest <= threshold && localBest < bestDist) {
                        bestDist = localBest;
                        bestCand = d;
                    }
                }

                return bestCand ? bestCand.user_id : '';
            };
            
            const findTeamId = (teamName: string) => {
                if (!teamName) return null;
                const lower = teamName.toLowerCase();
                const found = teams.find(t => t.name.toLowerCase().includes(lower) || lower.includes(t.name.toLowerCase()));
                return found ? found.id : null;
            };

            const formatMs = (ms: number) => { if (!ms || ms <= 0) return ''; const min = Math.floor(ms / 60000); const sec = ((ms % 60000) / 1000).toFixed(3); return min > 0 ? `${min}:${sec.padStart(6, '0')}` : sec; };

            // 1. SPECIFIC QUALIFYING RESULTS BLOCK (High Priority)
            if (root.qualifying_results && Array.isArray(root.qualifying_results) && root.qualifying_results.length > 0) {
                const qResults = root.qualifying_results;
                const stints = root.tyre_stints || {};

                qResults.forEach((entry: any) => {
                    const pos = parseInt(entry.position);
                    if (pos <= 0 || pos > 20) return;

                    const userId = findDriverId(entry.name);
                    let teamId = null;
                    if (!userId && entry.team_name) teamId = findTeamId(entry.team_name);

                    // FIX: Ensure this is mapped to quali_time, regardless of active tab
                    const timeVal = entry.best_lap_time_str || '';
                    
                    // Fetch Stints via Index from original JSON
                    const idxStr = String(entry.index);
                    const tStints: { tyre: string, laps: number }[] = [];
                    const rawStints = stints[idxStr] || stints[entry.index];
                    
                    if (rawStints) {
                        const completed = rawStints.completed_stints || [];
                        completed.forEach((s: any) => { if (s.laps > 0) tStints.push({ tyre: s.compound, laps: s.laps }); });
                        if (rawStints.current_compound && rawStints.current_stint_laps > 0) {
                            tStints.push({ tyre: rawStints.current_compound, laps: rawStints.current_stint_laps });
                        }
                    }

                    // Populate Slot
                    newSlots[pos - 1] = {
                        ...newSlots[pos - 1],
                        user_id: userId,
                        team_id: teamId,
                        status: 'FINISHED', 
                        quali_status: 'FINISHED',
                        quali_time: timeVal, // Force into quali_time
                        time_diff: '', // Clear race time to avoid confusion
                        telemetry_data: {
                            laps: [], 
                            stints: tStints,
                            penalties: 0,
                            warnings: 0
                        }
                    };
                });
            }
            // 2. NEW FORMAT (Python Relay)
            else if (root.leaderboard && Array.isArray(root.leaderboard)) {
                const results = root.leaderboard;
                // Pre-sort leaderboard just in case
                results.sort((a:any, b:any) => a.position - b.position);

                let flIndex = -1, bestLapGlobal = Infinity;

                results.forEach((entry: any, index: number) => {
                    const pos = parseInt(entry.position);
                    if (pos <= 0 || pos > 20) return;
                    
                    const userId = findDriverId(entry.name);
                    let teamId = null;
                    if (!userId && entry.team) teamId = findTeamId(entry.team);
                    
                    // Status
                    let status = 'FINISHED';
                    if (entry.isOut) {
                        status = 'DNF';
                        if (entry.outReason === 'DSQ') status = 'DSQ';
                    }
                    else if (entry.resultStatus && entry.resultStatus.toLowerCase() === 'dsq') status = 'DSQ';
                    
                    // Time Gap
                    let timeVal = '';
                    if (activeTab === 'RACE') {
                        timeVal = entry.gap || '';
                    } else {
                        // Quali/Sprint use best lap usually or gap if available
                        timeVal = entry.bestLapStr || '';
                    }

                    // Best Lap Tracking
                    if (entry.bestLap && entry.bestLap > 0 && entry.bestLap < bestLapGlobal) {
                        bestLapGlobal = entry.bestLap;
                        flIndex = index;
                    }

                    // Stints reconstruction
                    const tStints: { tyre: string, laps: number }[] = [];
                    if (entry.stints && Array.isArray(entry.stints)) {
                         entry.stints.forEach((s:any) => tStints.push({tyre: s.tyre, laps: s.laps}));
                    } else {
                         tStints.push({ tyre: entry.tyre, laps: entry.tyreAge });
                    }

                    const validLapsSec = entry.laps || [];
                    
                    newSlots[pos - 1] = {
                        ...newSlots[pos - 1],
                        user_id: userId,
                        team_id: teamId,
                        status: status as any,
                        time_diff: activeTab === 'RACE' ? timeVal : '',
                        quali_time: activeTab === 'QUALI' ? timeVal : '',
                        sprint_time: activeTab === 'SPRINT' ? timeVal : '',
                        quali_status: activeTab === 'QUALI' ? status : 'FINISHED',
                        sprint_status: activeTab === 'SPRINT' ? status : 'FINISHED',
                        telemetry_data: {
                            laps: validLapsSec,
                            stints: tStints,
                            penalties: entry.penalties || 0,
                            warnings: entry.warnings || 0,
                            consistency: calculateStdDev(validLapsSec),
                            race_pace: validLapsSec.length > 0 ? validLapsSec.reduce((a:number,b:number)=>a+b,0)/validLapsSec.length : 0,
                            lap_history: entry.lap_history || []
                        },
                        fastest_lap: false,
                        clean_race_bonus: (status === 'FINISHED' && (entry.penalties || 0) === 0)
                    };
                });

                if (flIndex !== -1) {
                    const f = results[flIndex];
                    if (f && f.position <= 20) newSlots[f.position - 1].fastest_lap = true;
                }

            } 
            // 3. OLD FORMAT (UDP Dumper)
            else if (root.final_classification && root.participants) {
                const participants = root.participants;
                const results = root.final_classification.classifications;
                const history = root.session_history || {};
                const stints = root.tyre_stints || {};
                let flIndex = -1, bestLapGlobal = Infinity;
                const leaderEntry = results.find((r:any) => parseInt(r.position) === 1);
                const leaderMs = leaderEntry ? (leaderEntry.total_race_time_ms || leaderEntry.total_race_time * 1000) : 0;
                const leaderLaps = leaderEntry ? leaderEntry.num_laps : 0;

                results.forEach((entry: any) => {
                    const pIdx = entry.index;
                    const pInfo = participants.find((p: any) => p.index === pIdx);
                    if (!pInfo) return;
                    const pos = parseInt(entry.position);
                    if (pos <= 0 || pos > 20) return;
                    const userId = findDriverId(pInfo.name);
                    
                    let teamId = null;
                    if (!userId && pInfo.team) teamId = findTeamId(pInfo.team) || findTeamId(pInfo.team_name);
                    else if (pInfo.team) teamId = findTeamId(pInfo.team) || findTeamId(pInfo.team_name);

                    let status = (entry.result_status === 4 || entry.result_status === 7 || entry.result_status === 6) ? 'DNF' : (entry.result_status === 5 ? 'DSQ' : 'FINISHED');
                    let timeVal = '';
                    const myMs = entry.total_race_time_ms || entry.total_race_time * 1000;
                    if (activeTab === 'RACE' && status === 'FINISHED') {
                        if (pos === 1) timeVal = formatMs(myMs);
                        else if (leaderLaps > entry.num_laps) timeVal = `+${leaderLaps - entry.num_laps} Laps`;
                        else timeVal = `+${((myMs - leaderMs)/1000).toFixed(3)}`;
                    } else if (activeTab === 'QUALI') { timeVal = formatMs(entry.best_lap_time_ms); }
                    if (entry.best_lap_time_ms > 0 && entry.best_lap_time_ms < bestLapGlobal) { bestLapGlobal = entry.best_lap_time_ms; flIndex = pIdx; }
                    
                    const dHist = history[String(pIdx)] || history[pIdx] || {};
                    const rawLaps = dHist.laps || dHist.lap_times || [];
                    const validLapsSec = rawLaps.map((l:any) => (typeof l === 'object' ? l.lap_time_ms : l) / 1000).filter((s:number) => s > 0);
                    let bestS1 = Infinity, bestS2 = Infinity, bestS3 = Infinity;
                    rawLaps.forEach((l:any) => { if (typeof l === 'object') { if (l.sector1_time_ms > 0 && l.sector1_time_ms < bestS1) bestS1 = l.sector1_time_ms; if (l.sector2_time_ms > 0 && l.sector2_time_ms < bestS2) bestS2 = l.sector2_time_ms; if (l.sector3_time_ms > 0 && l.sector3_time_ms < bestS3) bestS3 = l.sector3_time_ms; } });
                    
                    const tStints: { tyre: string, laps: number }[] = [];
                    const rawStints = stints[String(pIdx)] || stints[pIdx];
                    if (rawStints) {
                        const completed = rawStints.completed_stints || [];
                        completed.forEach((s: any) => { 
                            if (s.laps > 0) tStints.push({ tyre: s.compound, laps: s.laps }); 
                        });
                        if (rawStints.current_compound) {
                            const curLaps = rawStints.current_stint_laps || 0;
                            if (curLaps > 0) tStints.push({ tyre: rawStints.current_compound, laps: curLaps });
                        }
                    }
                    if (tStints.length === 0 && entry.tyre_stints_visual) {
                        entry.tyre_stints_visual.forEach((t: string) => tStints.push({ tyre: t, laps: 0 }));
                    }

                    const penaltiesTime = entry.penalties_time || 0;
                    
                    newSlots[pos - 1] = { 
                        ...newSlots[pos - 1], 
                        user_id: userId,
                        team_id: teamId, 
                        status: status as any, 
                        time_diff: activeTab === 'RACE' ? timeVal : '', 
                        quali_time: activeTab === 'QUALI' ? timeVal : '', 
                        sprint_time: activeTab === 'SPRINT' ? timeVal : '', 
                        quali_status: activeTab === 'QUALI' ? status : 'FINISHED', 
                        sprint_status: activeTab === 'SPRINT' ? status : 'FINISHED', 
                        telemetry_data: { laps: validLapsSec, stints: tStints, penalties: penaltiesTime, warnings: entry.total_warnings || 0, best_sectors: { s1: bestS1, s2: bestS2, s3: bestS3 }, theoretical_best: (bestS1 + bestS2 + bestS3) / 1000, consistency: calculateStdDev(validLapsSec), race_pace: validLapsSec.length > 0 ? validLapsSec.reduce((a:number,b:number)=>a+b,0)/validLapsSec.length : 0 }, 
                        fastest_lap: false,
                        clean_race_bonus: (status === 'FINISHED' && penaltiesTime === 0)
                    };
                });
                if (flIndex !== -1) { const f = results.find((r:any) => r.index === flIndex); if (f && f.position <= 20) newSlots[f.position - 1].fastest_lap = true; }
            }
            setSlots(newSlots); setShowTelemetryModal(false); alert("Import fertig! Speichere jetzt, um die Daten zu sichern.");
        } catch (e: any) { alert("Import Fehler: " + e.message); } finally { setLoading(false); }
    };

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Official Classification"
                title="Ergebnisse"
                description="Qualifying, Sprint und Rennen erfassen, Telemetriedaten übernehmen und die offizielle Wertung speichern."
                icon={Flag}
                accent="#e10600"
                stats={[{ label: 'Rennen', value: events.length }, { label: 'Fahrerplätze', value: slots.length }, { label: 'Belegt', value: slots.filter(slot => slot.user_id).length }]}
            />
            <Card className="rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-400 uppercase mb-2">Saison wählen</label>
                        <select 
                            className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white text-lg font-bold outline-none focus:border-f1-red"
                            value={selectedSeasonId}
                            onChange={(e) => {
                                setSelectedSeasonId(e.target.value);
                                setSelectedEventId('');
                                setSlots([]);
                            }}
                        >
                            <option value="">Aktive Saison</option>
                            {seasons.map(s => (
                                <option key={s.id} value={s.id}>{s.name} ({s.year})</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-400 uppercase mb-2">Event wählen</label>
                        <select 
                            className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white text-lg font-bold outline-none focus:border-f1-red" 
                            value={selectedEventId} 
                            onChange={e => loadResults(e.target.value)}
                        >
                            <option value="">-- Rennen wählen --</option>
                            {events.map(ev => (
                                <option key={ev.id} value={ev.id}>
                                    R{ev.roundNumber}: {ev.trackLocation} {ev.isSprint ? '(SPRINT)' : ''} {ev.status === 'COMPLETED' ? '✅' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {selectedEventId && (
                    <>
                        <div className="flex flex-col md:flex-row justify-between items-center mb-4 border-b border-gray-700 pb-4 gap-4">
                            <div className="flex gap-2">
                                <button onClick={() => setActiveTab('QUALI')} className={`px-4 py-2 text-sm font-bold uppercase rounded-t-lg transition-colors flex items-center gap-2 ${activeTab === 'QUALI' ? 'bg-gray-800 text-white border-t border-x border-gray-700' : 'text-gray-500 hover:text-white'}`}><Timer size={14}/> Qualifying</button>
                                {events.find(e => e.id === selectedEventId)?.isSprint && <button onClick={() => setActiveTab('SPRINT')} className={`px-4 py-2 text-sm font-bold uppercase rounded-t-lg transition-colors flex items-center gap-2 ${activeTab === 'SPRINT' ? 'bg-gray-800 text-white border-t border-x border-gray-700' : 'text-gray-500 hover:text-white'}`}><Zap size={14}/> Sprint</button>}
                                <button onClick={() => setActiveTab('RACE')} className={`px-4 py-2 text-sm font-bold uppercase rounded-t-lg transition-colors flex items-center gap-2 ${activeTab === 'RACE' ? 'bg-gray-800 text-white border-t border-x border-gray-700' : 'text-gray-500 hover:text-white'}`}><Flag size={14}/> Rennen</button>
                            </div>
                            <div className="flex gap-2">
                                {/* Reset Button */}
                                <Button variant="danger" size="sm" icon={Trash2} onClick={handleReset}>Zurücksetzen</Button>

                                {/* CSV Upload Button */}
                                <input type="file" ref={csvInputRef} onChange={handleCsvUpload} className="hidden" accept=".csv" />
                                <Button variant="secondary" size="sm" icon={FileSpreadsheet} onClick={() => csvInputRef.current?.click()}>CSV hochladen</Button>
                                
                                <Button variant="secondary" size="sm" icon={Database} onClick={loadTelemetrySessions}>Telemetrie</Button>
                                <Button onClick={handleSave} disabled={saving} size="sm">{saving ? '...' : 'Speichern'}</Button>
                            </div>
                        </div>
                        {loading ? <div className="text-center p-20"><RefreshCw className="animate-spin mx-auto mb-2 text-f1-red"/> Lade...</div> : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2">
                                {slots.map((slot, idx) => (
                                    <ResultRowSlot 
                                        key={idx} 
                                        position={idx + 1} 
                                        data={slot}
                                        pool={allDriversPool.filter(d => !slots.some((s, sidx) => sidx !== idx && s.user_id === d.user_id))} 
                                        teams={teams}
                                        activeTab={activeTab} 
                                        onChange={(f, v) => handleSlotChange(idx, f, v)}
                                        onMove={(targetPos) => moveSlotToPosition(idx, targetPos)}
                                        totalFilled={slots.filter(s => s.user_id).length}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Card>
            {showTelemetryModal && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg bg-f1-dark border-gray-600">
                        <div className="flex justify-between items-center mb-6 border-b border-gray-700 pb-2">
                            <h3 className="text-xl font-bold uppercase text-white">Archives</h3>
                            <button onClick={() => setShowTelemetryModal(false)}><X className="text-gray-400 hover:text-white"/></button>
                        </div>
                        
                        {/* MANUAL UPLOAD AREA */}
                        <div className="mb-6 p-4 bg-gray-900/50 rounded border border-gray-700">
                            <h4 className="text-sm font-bold text-gray-300 uppercase mb-2 flex items-center gap-2"><Upload size={14}/> Manuell hochladen</h4>
                            <p className="text-xs text-gray-500 mb-2">Lade eine .json Datei direkt in die Datenbank.</p>
                            <input type="file" ref={jsonInputRef} className="hidden" accept=".json" onChange={handleJsonUpload} />
                            <Button size="sm" variant="secondary" className="w-full" onClick={() => jsonInputRef.current?.click()}>Datei wählen</Button>
                        </div>

                        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                            {telemetrySessions.map(s => (
                                <div key={s.id} className="flex justify-between items-center p-3 bg-gray-900 border border-gray-700 rounded hover:bg-gray-800 cursor-pointer" onClick={() => importTelemetrySession(s.id)}>
                                    <div>
                                        <div className="font-bold text-white flex items-center gap-2">
                                            {s.name}
                                            {s.linked_event_id && <span className="text-[10px] bg-yellow-900/50 text-yellow-500 px-1 rounded border border-yellow-700">USED</span>}
                                        </div>
                                        <div className="text-xs text-gray-500 flex gap-2">
                                            <span>{new Date(s.created_at).toLocaleString()}</span>
                                            {s.size_bytes && <span>• {(s.size_bytes / 1024 / 1024).toFixed(2)} MB</span>}
                                        </div>
                                    </div>
                                    <Download size={16} className="text-gray-400"/>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
